import type { JournalArticle } from "./journal-types";
import { airportDepartureGuidance } from "./travel-guidance";

/** Cluster: prijs, facturatie en wijzigen. Bestaande slugs blijven behouden. */
export const PRIJS_ARTICLES: JournalArticle[] = [
  {
    slug: "vaste-prijs-versus-taxameter",
    title: "Vaste prijs of taxameter: wat betaalt u werkelijk?",
    seoTitle: "Vaste prijs of taxameter: wat betaalt u echt? | Taxiritje.nl",
    description:
      "Het verschil tussen een vaste ritprijs en een taxameter, en waarom u bij een vooraf bekende prijs niet meebetaalt aan files en omleidingen.",
    category: "prijs",
    intent: "Commercieel onderzoek: bezoeker wil weten welke prijsvorm gunstiger is.",
    primaryKeyword: "vaste prijs of taxameter",
    published: "2026-02-10",
    updated: "2026-08-22",
    readingMinutes: 4,
    intro:
      "Rijdt u met een taxameter, dan telt elke minuut stilstaan mee. Met een vaste prijs weet u vooraf precies wat de rit kost, ongeacht het verkeer.",
    sections: [
      {
        heading: "Hoe een taxameter rekent",
        paragraphs: [
          "Een taxameter combineert een starttarief met een bedrag per kilometer en een bedrag per minuut. Staat u stil in de file, dan loopt het bedrag toch door. Bij lange ritten kan het verschil tussen een rustige en een drukke rit daardoor flink oplopen.",
        ],
      },
      {
        heading: "Hoe een vaste prijs werkt",
        paragraphs: [
          "Bij een vaste prijs wordt de route vooraf berekend op basis van het opgegeven ophaaladres en de bestemming. Die prijs staat vast zodra u boekt. Wijzigt u zelf iets aan de rit, bijvoorbeeld een ander adres of een extra tussenstop, dan wordt de prijs opnieuw berekend en ziet u het verschil voordat u bevestigt.",
        ],
        bullets: [
          "Prijs vooraf bekend, ook bij drukte of omleidingen",
          "Duidelijk verschil zichtbaar bij een enkele rit of retour",
          "Wijzigingen worden altijd eerst voorgerekend",
        ],
      },
      {
        heading: "Waar u op moet letten",
        paragraphs: [
          "Vraag altijd of de prijs inclusief btw is en of wachttijd bij de luchthaven is meegerekend. Vergelijk niet alleen het bedrag, maar ook wat erin zit: bagage, tussenstops en wachttijd bepalen samen of een prijs echt vast is.",
        ],
      },
    ],
    conclusion:
      "Een vaste prijs neemt het risico van vertraging bij u weg: het bedrag staat vast op het moment van boeken, niet aan het einde van de rit.",
    faq: [
      {
        q: "Betaal ik bij een vaste prijs meer als er file staat?",
        a: "Nee. Het bedrag staat vast zodra u boekt, ongeacht drukte of omleidingen.",
      },
      {
        q: "Wat gebeurt er als ik het adres wijzig?",
        a: "De prijs wordt opnieuw berekend en u ziet het verschil voordat u de wijziging bevestigt.",
      },
    ],
    links: [
      { label: "Bekijk uw vaste prijs en boek uw rit", to: "/boeken" },
      { label: "Prijzen per route bekijken", to: "/prijzen" },
      { label: "Taxi tijdens de spits", to: "/journal/taxi-tijdens-de-spits" },
    ],
  },
  {
    slug: "luchthaventaxi-hoe-laat-vertrekken",
    title: "Luchthaventaxi: hoe laat moet u vertrekken?",
    seoTitle: "Luchthaventaxi: hoe laat moet u vertrekken? | Taxiritje.nl",
    description:
      "Praktische richtlijnen voor uw vertrektijd naar de luchthaven, inclusief marge voor verkeer, bagage en check-in.",
    category: "luchthaven",
    intent: "Informatief: reiziger wil een veilige vertrektijd bepalen.",
    primaryKeyword: "hoe laat vertrekken naar het vliegveld",
    published: "2026-03-04",
    updated: "2026-08-22",
    readingMinutes: 5,
    intro:
      airportDepartureGuidance("nl"),
    sections: [
      {
        heading: "Reken terug vanaf uw vlucht",
        paragraphs: [
          "Neem voor Europese vluchten ongeveer twee uur voor vertrek als aanwezigheidstijd, en voor intercontinentale vluchten drie uur. Tel daar de reistijd naar de luchthaven bij op, plus extra marge voor de ochtend- en avondspits.",
        ],
        table: {
          caption: "Vuistregel voor aanwezigheid op de luchthaven",
          columns: ["Type vlucht", "Aanwezig vóór vertrek"],
          rows: [
            ["Europese vlucht, alleen handbagage", "ongeveer 2 uur"],
            ["Europese vlucht met ruimbagage", "2 tot 2,5 uur"],
            ["Intercontinentale vlucht", "ongeveer 3 uur"],
          ],
        },
      },
      {
        heading: "Waarom een vluchtnummer belangrijk is",
        paragraphs: [
          "Bij een rit naar of van een luchthaven vragen wij altijd uw vluchtnummer. Bij een terugreis werken wij met uw landingstijd in plaats van een ophaaltijd, zodat de planning past bij het moment dat u daadwerkelijk aankomt.",
        ],
      },
      {
        heading: "Bagage en inzittenden",
        paragraphs: [
          "Geef bij het boeken het aantal grote koffers en handbagagestukken op. Bij veel bagage of meer dan vier inzittenden wordt automatisch een groter voertuigtype voorgesteld, zodat alles daadwerkelijk meekan.",
        ],
      },
    ],
    conclusion:
      "Reken terug vanaf de aanwezigheidstijd, tel de reistijd plus spitsmarge op en boek uw rit met vluchtnummer. Dan begint uw reis zonder haast.",
    faq: [
      {
        q: "Hoeveel marge moet ik aanhouden voor de spits?",
        a: "Reken tijdens de ochtend- en avondspits op twintig tot dertig minuten extra reistijd.",
      },
      {
        q: "Waarom is een vluchtnummer nodig?",
        a: "Bij de terugreis wordt op uw landingstijd gepland in plaats van op een vast ophaalmoment, zodat de rit aansluit op uw werkelijke aankomst.",
      },
    ],
    links: [
      { label: "Luchthaventaxi met vaste prijs", to: "/luchthaventaxi" },
      { label: "Taxi bij een vroege vlucht", to: "/journal/taxi-naar-het-vliegveld-bij-een-vroege-vlucht" },
      { label: "Rit boeken", to: "/boeken" },
    ],
  },
  {
    slug: "zakelijk-taxivervoer-facturatie",
    title: "Zakelijk taxivervoer: zo houdt u uw facturatie op orde",
    seoTitle: "Zakelijk taxivervoer en facturatie met btw | Taxiritje.nl",
    description:
      "Hoe u zakelijke taxiritten met btw en bedrijfsgegevens administratief eenvoudig verwerkt, van factuurgegevens tot maandoverzicht.",
    category: "prijs",
    intent: "Commercieel/praktisch: zakelijke reiziger wil kloppende facturen.",
    primaryKeyword: "zakelijk taxivervoer factuur",
    published: "2026-04-18",
    updated: "2026-08-22",
    readingMinutes: 3,
    intro:
      "Zakelijke ritten vragen om een factuur die uw boekhouding zonder vragen accepteert: juiste bedrijfsgegevens, btw en een herkenbare rit.",
    sections: [
      {
        heading: "Bewaar uw factuurgegevens één keer",
        paragraphs: [
          "In uw account legt u uw bedrijfsnaam, adres en btw-nummer eenmalig vast. Elke volgende rit gebruikt automatisch diezelfde gegevens, zodat u ze niet per boeking hoeft in te vullen.",
        ],
      },
      {
        heading: "Wat er op de factuur staat",
        paragraphs: [
          "Een factuur bevat het ritnummer, de datum en tijd van ophalen, de route, het aantal inzittenden en het bedrag met btw-specificatie. Wijzigt de rit nog, dan wordt de factuur opnieuw opgebouwd met het definitieve bedrag.",
        ],
      },
      {
        heading: "Meerdere ritten, één overzicht",
        paragraphs: [
          "In uw account vindt u al uw ritten met hun status en factuur bij elkaar. Zo kunt u aan het einde van de maand in één keer terugvinden welke ritten er zijn gemaakt.",
        ],
      },
    ],
    conclusion:
      "Leg uw bedrijfsgegevens één keer vast en u haalt elke factuur met btw-specificatie zelf op uit uw account.",
    faq: [
      {
        q: "Staat de btw apart op de factuur?",
        a: "Ja, de factuur bevat een btw-specificatie naast het totaalbedrag en de ritgegevens.",
      },
      {
        q: "Kan ik facturen van eerdere ritten terugvinden?",
        a: "In uw account staan al uw ritten met status en bijbehorende factuur bij elkaar.",
      },
    ],
    links: [
      { label: "Zakelijk taxivervoer", to: "/zakelijk" },
      { label: "Rit boeken op factuur", to: "/boeken" },
      { label: "Met de taxi naar een hotel", to: "/journal/taxi-naar-een-hotel" },
    ],
  },
  {
    slug: "rit-wijzigen-of-annuleren",
    title: "Rit wijzigen of annuleren: hoe werkt het?",
    seoTitle: "Taxirit wijzigen of annuleren: hoe werkt het? | Taxiritje.nl",
    description:
      "Wat u zelf online kunt aanpassen aan een geboekte taxirit, en wat er gebeurt binnen 24 uur voor het ophalen.",
    category: "timing-en-reserveren",
    intent: "Probleemoplossend: klant wil een geboekte rit aanpassen.",
    primaryKeyword: "taxirit wijzigen of annuleren",
    published: "2026-05-22",
    updated: "2026-08-22",
    readingMinutes: 3,
    intro:
      "Plannen veranderen. Tot 24 uur voor het ophalen past u uw rit zelf online aan; daarna helpen wij u telefonisch verder.",
    sections: [
      {
        heading: "Zelf online aanpassen",
        paragraphs: [
          "In uw account opent u de rit en past u datum, tijd, adressen, vluchtnummer, bagage of het aantal inzittenden aan. Leidt de wijziging tot een hoger bedrag, dan ziet u het verschil en rondt u dat eerst af. Wordt de rit goedkoper, dan wordt het verschil aan u terugbetaald.",
        ],
      },
      {
        heading: "Binnen 24 uur voor het ophalen",
        paragraphs: [
          "Vlak voor de rit is de planning al definitief gemaakt. Neem in dat geval telefonisch contact met ons op; wijzigingen zijn dan afhankelijk van de mogelijkheden op dat moment.",
        ],
      },
      {
        heading: "Annuleren",
        paragraphs: [
          "Annuleren kan online tot 24 uur voor het ophaalmoment. De voorwaarden en eventuele kosten leest u in ons annuleringsbeleid.",
        ],
      },
    ],
    conclusion:
      "Online wijzigen en annuleren kan tot 24 uur voor het ophaalmoment; daarbinnen regelt u het telefonisch.",
    faq: [
      {
        q: "Tot wanneer kan ik mijn rit online wijzigen?",
        a: "Tot 24 uur voor het ophaalmoment. Daarna verloopt een wijziging telefonisch.",
      },
      {
        q: "Krijg ik geld terug als de rit goedkoper wordt?",
        a: "Ja, het verschil wordt aan u terugbetaald nadat de wijziging is verwerkt.",
      },
    ],
    links: [
      { label: "Annuleringsbeleid bekijken", to: "/annuleringsbeleid" },
      { label: "Hoe lang van tevoren reserveren?", to: "/journal/hoe-lang-van-tevoren-taxi-reserveren" },
      { label: "Contact opnemen", to: "/contact" },
    ],
  },
];
